"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import { X } from "@app-foundation/icons/lucide";
import { Dialog } from "@app-foundation/ui-controls";
import "@app-foundation/ui-controls/styles.css";
import styles from "./regional-agent.module.css";

export function AgentApplication() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const request = useRef<{ id: string; fingerprint: string } | null>(null);
  const titleId = useId();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const values = Object.fromEntries(["contactName", "phone", "city", "company", "resources"].map(key => [key, String(data.get(key) ?? "").trim()]));
    const fingerprint = JSON.stringify(values);
    if (!request.current || request.current.fingerprint !== fingerprint) request.current = { id: crypto.randomUUID(), fingerprint };
    setBusy(true); setMessage("");
    try {
      const response = await fetch("/api/regional-agent-applications", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...values, requestId: request.current.id }) });
      const result = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (!response.ok || result?.ok !== true) throw new Error(result?.error || "申请暂未提交成功，请稍后重试");
      setSubmitted(true); request.current = null; form.reset();
    } catch (error) { setMessage(error instanceof Error ? error.message : "申请暂未提交成功，请稍后重试"); }
    finally { setBusy(false); }
  }
  function setDialogOpen(value: boolean) { if (busy) return; setOpen(value); if (!value) { setMessage(""); setSubmitted(false); } }
  return <>
    <button type="button" className={`${styles.btn} ${styles["btn-primary"]}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
      申请成为区域代理 <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.5 7h9M8 3.5L11.5 7 8 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
    <Dialog open={open} onOpenChange={setDialogOpen} aria-labelledby={titleId} className={styles.applicationDialog}>
      <button type="button" className={styles.applicationClose} aria-label="关闭区域代理申请" onClick={() => setDialogOpen(false)} disabled={busy}><X size={20} /></button>
      <h2 id={titleId}>区域代理合作申请</h2>
      <div>
        {submitted ? <p role="status">申请已提交，我们会与您联系。</p> : <form className={styles.applicationForm} onSubmit={submit}>
          <label>联系人姓名 <span>*</span><input required name="contactName" maxLength={80} autoComplete="name" placeholder="请输入联系人姓名" /></label>
          <label>联系手机号 <span>*</span><input required name="phone" type="tel" inputMode="numeric" autoComplete="tel" pattern="1[3-9][0-9]{9}" maxLength={11} placeholder="请输入 11 位手机号" /></label>
          <label>拟合作城市 <span>*</span><input required name="city" maxLength={80} placeholder="请输入省份及城市" /></label>
          <label>企业或个体工商户名称 <span>*</span><input required name="company" maxLength={160} placeholder="请输入企业或个体工商户全称" /></label>
          <label>场地与合作资源说明 <span>*</span><textarea required name="resources" maxLength={2000} rows={4} placeholder="可简述场地、团队、设备或当地资源" /></label>
          <p className={styles.applicationPrivacy}>提交即表示您了解我们将使用上述联系方式与您沟通合作。<a href="/agreements/privacy" target="_blank" rel="noreferrer">隐私政策</a></p>
          {message && <p role="alert">{message}</p>}
          <button type="submit" className={`${styles.btn} ${styles["btn-primary"]}`} disabled={busy}>{busy ? "提交中…" : "提交申请"}</button>
        </form>}
      </div>
    </Dialog>
  </>;
}
